import { useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { dashboardStyles } from "@/components/dashboard/shared/dashboard-styles";
import { EmailPreviewFrame } from "@/components/admin/EmailPreviewFrame";
import {
  CAMPAIGN_TEMPLATES,
  buildCampaignBodyHtml,
  emptyCampaignFields,
  wrapBrandedEmailHtml,
  type CampaignContentFields,
  type CampaignTemplateId,
} from "@/lib/campaign-email";
import { uploadCampaignImage } from "@/utils/cloudinary";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  ArrowLeft,
  ImagePlus,
  Loader2,
  Monitor,
  Save,
  Send,
  Smartphone,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";

export type CampaignFormState = {
  name: string;
  subject: string;
  content: string;
  recipientType: "all_users" | "store_owners" | "managers" | "customers" | "custom";
  customRecipients: string;
  testEmail: string;
};

type CampaignComposerProps = {
  title: string;
  form: CampaignFormState;
  fields: CampaignContentFields;
  saving?: boolean;
  sendingTest?: boolean;
  onChangeForm: (patch: Partial<CampaignFormState>) => void;
  onChangeFields: (next: CampaignContentFields) => void;
  onClose: () => void;
  onSave: () => void;
  onSendTest: () => void;
};

const RECIPIENTS = [
  { value: "all_users", label: "All users" },
  { value: "store_owners", label: "Store owners" },
  { value: "managers", label: "Managers" },
  { value: "customers", label: "Customers" },
  { value: "custom", label: "Custom list" },
] as const;

export function CampaignComposer({
  title,
  form,
  fields,
  saving,
  sendingTest,
  onChangeForm,
  onChangeFields,
  onClose,
  onSave,
  onSendTest,
}: CampaignComposerProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const extraFileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState<"hero" | "extra" | null>(null);
  const [viewport, setViewport] = useState<"desktop" | "mobile">("desktop");
  const [mobileTab, setMobileTab] = useState<"write" | "preview">("write");

  const applyTemplate = (id: CampaignTemplateId) => {
    const next = emptyCampaignFields(id);
    onChangeFields({
      ...next,
      heroImageUrl: fields.heroImageUrl,
      images: fields.images,
    });
    onChangeForm({ content: buildCampaignBodyHtml({ ...next, heroImageUrl: fields.heroImageUrl, images: fields.images }) });
  };

  const updateFields = (patch: Partial<CampaignContentFields>) => {
    const next = { ...fields, ...patch };
    onChangeFields(next);
    onChangeForm({ content: buildCampaignBodyHtml(next) });
  };

  const upload = async (file: File, kind: "hero" | "extra") => {
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    setUploading(kind);
    try {
      const result = await uploadCampaignImage(file);
      if (kind === "hero") {
        updateFields({ heroImageUrl: result.url });
      } else {
        updateFields({ images: [...fields.images, result.url] });
      }
      toast.success("Image added");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Upload failed");
    } finally {
      setUploading(null);
    }
  };

  const previewHtml = useMemo(
    () =>
      wrapBrandedEmailHtml(form.content || buildCampaignBodyHtml(fields), {
        title: form.subject || "Preview",
        preheader: form.subject || "Preview",
      }),
    [form.content, form.subject, fields],
  );

  const editor = (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>Campaign name</Label>
          <Input
            value={form.name}
            onChange={(e) => onChangeForm({ name: e.target.value })}
            placeholder="Spring launch"
            className={dashboardStyles.filterInput.replace("pl-9", "px-4")}
          />
        </div>
        <div className="space-y-2">
          <Label>Subject line</Label>
          <Input
            value={form.subject}
            onChange={(e) => onChangeForm({ subject: e.target.value })}
            placeholder="What’s new this week"
            className={dashboardStyles.filterInput.replace("pl-9", "px-4")}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Recipients</Label>
        <Select
          value={form.recipientType}
          onValueChange={(value) =>
            onChangeForm({
              recipientType: value as CampaignFormState["recipientType"],
            })
          }
        >
          <SelectTrigger className={cn(dashboardStyles.filterSelect, "w-full")}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RECIPIENTS.map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {form.recipientType === "custom" && (
        <div className="space-y-2">
          <Label>Custom list</Label>
          <Textarea
            value={form.customRecipients}
            onChange={(e) => onChangeForm({ customRecipients: e.target.value })}
            placeholder={"Ada, ada@email.com\nTunde, tunde@email.com"}
            rows={4}
            className="min-h-[96px] rounded-2xl border-[#EBEBEB] bg-[#F5F5F3] shadow-none"
          />
          <p className="text-xs text-muted-foreground">One per line: Name, email</p>
        </div>
      )}

      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <Label>Template</Label>
        </div>
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
          {CAMPAIGN_TEMPLATES.map((template) => (
            <button
              key={template.id}
              type="button"
              onClick={() => applyTemplate(template.id)}
              className={cn(
                "rounded-2xl border px-3 py-3 text-left transition-colors",
                fields.templateId === template.id
                  ? "border-primary bg-[#EDE4FB]"
                  : "border-[#EBEBEB] bg-white hover:bg-[#F5F5F3]",
              )}
            >
              <p className="text-sm font-heading font-semibold">{template.label}</p>
              <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
                {template.description}
              </p>
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>Eyebrow</Label>
          <Input
            value={fields.eyebrow}
            onChange={(e) => updateFields({ eyebrow: e.target.value })}
            placeholder="Announcement"
            className={dashboardStyles.filterInput.replace("pl-9", "px-4")}
          />
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label>Headline</Label>
          <Input
            value={fields.headline}
            onChange={(e) => updateFields({ headline: e.target.value })}
            placeholder="Your headline"
            className={dashboardStyles.filterInput.replace("pl-9", "px-4")}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Message</Label>
        <Textarea
          value={fields.body}
          onChange={(e) => updateFields({ body: e.target.value })}
          rows={8}
          className="min-h-[160px] rounded-2xl border-[#EBEBEB] bg-[#F5F5F3] shadow-none"
        />
        <p className="text-xs text-muted-foreground">
          Use {"{{name}}"} to insert the recipient’s name. Images are hosted on Cloudinary and shown in the HTML.
        </p>
      </div>

      <div className="space-y-3">
        <Label>Images</Label>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void upload(file, "hero");
            e.target.value = "";
          }}
        />
        <input
          ref={extraFileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void upload(file, "extra");
            e.target.value = "";
          }}
        />
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            className="h-10 rounded-full px-4 text-sm font-semibold shadow-none"
            disabled={!!uploading}
            onClick={() => fileRef.current?.click()}
          >
            {uploading === "hero" ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <ImagePlus className="mr-2 h-4 w-4" />
            )}
            {fields.heroImageUrl ? "Replace hero" : "Add hero image"}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-10 rounded-full px-4 text-sm font-semibold shadow-none"
            disabled={!!uploading}
            onClick={() => extraFileRef.current?.click()}
          >
            {uploading === "extra" ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <ImagePlus className="mr-2 h-4 w-4" />
            )}
            Add image
          </Button>
        </div>
        {(fields.heroImageUrl || fields.images.length > 0) && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {fields.heroImageUrl ? (
              <div className="relative overflow-hidden rounded-2xl border border-[#EBEBEB]">
                <img src={fields.heroImageUrl} alt="Hero" className="h-28 w-full object-cover" />
                <span className="absolute left-2 top-2 rounded-full bg-white/90 px-2 py-0.5 text-[10px] font-semibold">
                  Hero
                </span>
                <button
                  type="button"
                  className="absolute right-2 top-2 rounded-full bg-white/90 p-1"
                  onClick={() => updateFields({ heroImageUrl: "" })}
                  aria-label="Remove hero image"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : null}
            {fields.images.map((url) => (
              <div key={url} className="relative overflow-hidden rounded-2xl border border-[#EBEBEB]">
                <img src={url} alt="" className="h-28 w-full object-cover" />
                <button
                  type="button"
                  className="absolute right-2 top-2 rounded-full bg-white/90 p-1"
                  onClick={() =>
                    updateFields({ images: fields.images.filter((item) => item !== url) })
                  }
                  aria-label="Remove image"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>Button label</Label>
          <Input
            value={fields.ctaLabel}
            onChange={(e) => updateFields({ ctaLabel: e.target.value })}
            placeholder="Open dashboard"
            className={dashboardStyles.filterInput.replace("pl-9", "px-4")}
          />
        </div>
        <div className="space-y-2">
          <Label>Button link</Label>
          <Input
            value={fields.ctaUrl}
            onChange={(e) => updateFields({ ctaUrl: e.target.value })}
            placeholder="https://"
            className={dashboardStyles.filterInput.replace("pl-9", "px-4")}
          />
        </div>
      </div>

      <div className="flex flex-col gap-2 rounded-[1.25rem] border border-[#EBEBEB] bg-[#F5F5F3] p-4 sm:flex-row sm:items-end">
        <div className="flex-1 space-y-2">
          <Label>Send a test</Label>
          <Input
            type="email"
            value={form.testEmail}
            onChange={(e) => onChangeForm({ testEmail: e.target.value })}
            placeholder="you@email.com"
            className={dashboardStyles.filterInput.replace("pl-9", "px-4")}
          />
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={onSendTest}
          disabled={sendingTest}
          className="h-10 rounded-full px-4 text-sm font-semibold shadow-none"
        >
          {sendingTest ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
          Send test
        </Button>
      </div>
    </div>
  );

  const preview = (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-heading font-semibold">Preview</p>
        <div className="flex rounded-full border border-[#EBEBEB] bg-white p-1">
          <button
            type="button"
            onClick={() => setViewport("desktop")}
            className={cn(
              "rounded-full px-3 py-1.5 text-xs font-semibold",
              viewport === "desktop" ? "bg-[#EDE4FB] text-primary" : "text-muted-foreground",
            )}
          >
            <Monitor className="mr-1 inline h-3.5 w-3.5" />
            Desktop
          </button>
          <button
            type="button"
            onClick={() => setViewport("mobile")}
            className={cn(
              "rounded-full px-3 py-1.5 text-xs font-semibold",
              viewport === "mobile" ? "bg-[#EDE4FB] text-primary" : "text-muted-foreground",
            )}
          >
            <Smartphone className="mr-1 inline h-3.5 w-3.5" />
            Mobile
          </button>
        </div>
      </div>
      <EmailPreviewFrame html={previewHtml} viewport={viewport} />
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="h-10 rounded-full px-4 text-sm font-semibold shadow-none"
          >
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            Campaigns
          </Button>
          <h1 className="font-heading text-xl font-bold tracking-tight sm:text-2xl">{title}</h1>
        </div>
        <Button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="h-10 rounded-full px-5 text-sm font-semibold shadow-none"
        >
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          Save draft
        </Button>
      </div>

      <div className="flex rounded-full border border-[#EBEBEB] bg-[#F5F5F3] p-1 lg:hidden">
        {(["write", "preview"] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setMobileTab(tab)}
            className={cn(
              "flex-1 rounded-full py-2 text-sm font-semibold capitalize",
              mobileTab === tab ? "bg-white text-foreground shadow-sm" : "text-muted-foreground",
            )}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.95fr)]">
        <div className={cn(dashboardStyles.sectionCard, mobileTab === "preview" && "hidden lg:block")}>
          {editor}
        </div>
        <div className={cn(mobileTab === "write" && "hidden lg:block")}>{preview}</div>
      </div>
    </div>
  );
}
